import {browserSessionStorage} from "core/browserStorage";

/**
 * Whether this browser session began on a lifecycle offer ladder link
 * (kitecorp/transferify-ui#2111, owner decision 2026-09-06).
 *
 * <b>The fact this answers is "how did this visit start", not "what is this link worth".</b> A reader
 * who followed a rung was already mailed that rung's offer — the day-7 message names 50% off a first
 * month — and the pricing dialog's exit offer is a BETTER deal on the same tier (Premium for 14 days
 * at the intro price, or a seven-day free trial). Putting the two in front of one reader undercuts the
 * message that brought them and spends the ladder's own conversion on a cheaper sale, so it stays
 * away for the rest of the session. The rule that reads this lives in `upsell/exitOffer`; this module
 * only remembers the arrival.
 *
 * <b>ANY rung the page can read, and whatever the link turned out to be worth.</b> The mark is written
 * from the address bar, before the api has priced the token — so an EXPIRED rung marks the visit, and
 * so does one the resolve request never answered for. The owner's rule is about how the visit started,
 * an expired rung is still a reader who was mailed an offer this week, and a transport failure says
 * nothing about the link.
 *
 * <b>What it does NOT cover, stated because the obvious reading is wrong:</b> a token the api does not
 * recognise never reaches this page carrying a parameter at all. `OfferLadderLinkService.followAndDestinationFor`
 * redirects an unknown token to bare `/pricing` with no `?offer=`, deliberately, so that probing cannot
 * learn which tokens ever existed — and `parseLadderToken` likewise answers null for an empty or
 * over-long value. Those visits are indistinguishable from any other and are not marked. That is
 * acceptable rather than merely unavoidable: nothing on their screen was priced by a rung, so the exit
 * offer undercuts no offer they can see. Covering them would take a change on the api side and an
 * owner decision about what that redirect may reveal.
 *
 * <b>The TOKEN is deliberately not what is stored.</b> `./ladderOffer` states why nothing about a
 * ladder link is persisted: the token is a bearer credential in a forwarded email that authorizes a
 * discount on one account, and a copy of it outliving the address bar is a credential nobody asked us
 * to keep. A boolean is not a credential — it authorizes nothing, names no account and buys nothing —
 * so it may be remembered where the token may not.
 *
 * <b>SESSION storage, which is the scope the rule names.</b> The owner's sentence is "for the rest of
 * that session": it must survive the SPA navigation between landing on `/pricing` and opening the
 * pricing dialog from any other route — component state and the route's own query cannot — and it must
 * NOT survive the tab, because a reader who comes back next week on their own is an ordinary visitor
 * again. It is also the scope the exit offer's own once-per-session cap already uses
 * (`upsell/offerDismissal`), so the two facts guarding one dialog expire together.
 *
 * <b>`sessionStorage` is per TAB, which is narrower than the word "session", and that is accepted.</b>
 * A reader who follows the rung link in one tab and opens the site in a second tab of the same browser
 * has no mark there and can meet the offer. The alternative is `localStorage`, which would carry the
 * suppression across days and withhold the offer long after the rung's own 72 hours are up — a worse
 * error, and one the reader cannot undo. The cap this rule sits beside makes exactly the same trade,
 * so a second tab already gets one extra showing of this dialog with or without this module.
 *
 * React-free by the repo's logic-outside-react mandate, for the reason `upsell/exitOffer` states: this
 * is a fact about the visit rather than view state, and the hooks beside it only read and write it.
 */

/** Versioned like every other stored offer fact, so a future shape change invalidates rather than misreads. */
export const LADDER_ARRIVAL_SESSION_STORAGE_KEY = "offers.ladder.arrival.v1";

/** The one value {@link recordLadderArrival} writes; anything else reads as "did not arrive on a link". */
const ARRIVED = "1";

/**
 * Whether this session started on a ladder link.
 *
 * Unreadable, absent and unexpected all answer `false`, which lets the exit offer through. The
 * direction is the same one `upsell/offerDismissal` takes for the session cap, and for the same
 * reason: a browser that cannot read this cannot have been written to either, so answering `true`
 * would silence the offer for every visitor on it permanently, with nothing on screen to undo it and
 * nothing to say it happened. Being wrong the other way shows one ladder reader one offer too many on
 * a browser where no ladder arrival could be recorded in the first place.
 */
export function arrivedFromLadderLink(): boolean {
	try {
		return browserSessionStorage()?.getItem(LADDER_ARRIVAL_SESSION_STORAGE_KEY) === ARRIVED;
	} catch {
		// See the note on the write below: `core/browserStorage` guards the property ACCESS, and a store
		// that hands itself over and then refuses the call throws from here instead. This one runs on the
		// pricing sheet's close handler, so an escaping exception would break the close itself.
		return false;
	}
}

/**
 * Records the arrival. Idempotent; a no-op where there is no session storage, and where there is one
 * that refuses the write.
 *
 * <b>The try/catch is not belt-and-braces.</b> `core/browserStorage` can only guard the property
 * ACCESS; a storage area that hands over the object and then throws from `setItem` — an exhausted
 * quota, some privacy modes — is invisible to it. This is called from an effect in `useLadderOffer`,
 * so an escaping exception arrives during React's effect flush and takes the whole `/pricing` route to
 * the error boundary, for exactly the reader this feature exists for. A LOST MARK is the acceptable
 * failure here (they see one offer too many); a blank pricing page is not. `upsell/checkoutHandoff`
 * guards its own session-storage write for the same reason.
 */
export function recordLadderArrival(): void {
	try {
		browserSessionStorage()?.setItem(LADDER_ARRIVAL_SESSION_STORAGE_KEY, ARRIVED);
	} catch {
		// Deliberately silent: nothing downstream can act on it, and the reader loses only the suppression.
	}
}
